import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Field, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Head, Link, useForm } from "@inertiajs/react";
import { AlertCircle, GalleryVerticalEnd, MailCheck } from "lucide-react";
import type { FormEvent } from "react";

type InvitationInfo = {
  email: string;
  role: string;
  organizationName?: string;
  inviterName: string;
};

type Props = {
  invitation?: InvitationInfo;
  userExists?: boolean;
  signedIn?: boolean;
  emailMismatch?: boolean;
  error?: string;
};

const roleLabels: Record<string, string> = {
  admin: "Administrador",
  member: "Membro",
  superadmin: "Super Administrador",
};

function inviteDescription(invitation: InvitationInfo) {
  const roleLabel = roleLabels[invitation.role] ?? invitation.role;
  if (invitation.role === "superadmin") {
    return (
      <>
        <strong>{invitation.inviterName}</strong> convidou você para ser{" "}
        <strong>{roleLabel}</strong> da plataforma.
      </>
    );
  }
  return (
    <>
      <strong>{invitation.inviterName}</strong> convidou você para{" "}
      <strong>{invitation.organizationName}</strong> como{" "}
      <strong>{roleLabel}</strong>.
    </>
  );
}

function ErrorCard({ title, description }: { title: string; description: string }) {
  return (
    <Card className="w-full max-w-md">
      <CardHeader className="text-center">
        <div className="mx-auto mb-2 flex size-12 items-center justify-center rounded-full bg-destructive/10">
          <AlertCircle className="size-6 text-destructive" />
        </div>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardFooter className="justify-center">
        <Button asChild variant="outline">
          <Link href="/session/new">Ir para login</Link>
        </Button>
      </CardFooter>
    </Card>
  );
}

function AcceptCard({ invitation }: { invitation: InvitationInfo }) {
  const { post, processing } = useForm({});

  function handleAccept(e: FormEvent) {
    e.preventDefault();
    post(window.location.pathname);
  }

  return (
    <Card className="w-full max-w-md">
      <CardHeader className="text-center">
        <div className="mx-auto mb-2 flex size-12 items-center justify-center rounded-full bg-primary/10">
          <MailCheck className="size-6 text-primary" />
        </div>
        <CardTitle>Aceitar Convite</CardTitle>
        <CardDescription>
          {inviteDescription(invitation)}
        </CardDescription>
      </CardHeader>
      <CardFooter className="justify-center">
        <form onSubmit={handleAccept}>
          <Button type="submit" disabled={processing}>
            Aceitar Convite
          </Button>
        </form>
      </CardFooter>
    </Card>
  );
}

function SignInCard({ invitation }: { invitation: InvitationInfo }) {
  return (
    <Card className="w-full max-w-md">
      <CardHeader className="text-center">
        <div className="mx-auto mb-2 flex size-12 items-center justify-center rounded-full bg-primary/10">
          <MailCheck className="size-6 text-primary" />
        </div>
        <CardTitle>Aceitar Convite</CardTitle>
        <CardDescription>
          {inviteDescription(invitation)}
        </CardDescription>
      </CardHeader>
      <CardContent className="text-center text-sm text-muted-foreground">
        Faça login com <strong>{invitation.email}</strong> para aceitar o convite.
      </CardContent>
      <CardFooter className="justify-center">
        <Button asChild>
          <Link href="/session/new">Entrar</Link>
        </Button>
      </CardFooter>
    </Card>
  );
}

function RegisterCard({ invitation }: { invitation: InvitationInfo }) {
  const { data, setData, post, processing, errors } = useForm({
    name: "",
    password: "",
    password_confirmation: "",
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    post(window.location.pathname);
  }

  return (
    <Card className="w-full max-w-md">
      <CardHeader className="text-center">
        <div className="mx-auto mb-2 flex size-12 items-center justify-center rounded-full bg-primary/10">
          <MailCheck className="size-6 text-primary" />
        </div>
        <CardTitle>Criar Conta e Aceitar Convite</CardTitle>
        <CardDescription>
          {inviteDescription(invitation)}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} id="register-form">
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="email">E-mail</FieldLabel>
              <Input id="email" value={invitation.email} disabled />
            </Field>
            <Field>
              <FieldLabel htmlFor="name">Nome</FieldLabel>
              <Input
                id="name"
                value={data.name}
                onChange={(e) => setData("name", e.target.value)}
                placeholder="Seu nome"
                required
              />
              {errors.name && (
                <p className="text-sm text-destructive">{errors.name}</p>
              )}
            </Field>
            <Field>
              <FieldLabel htmlFor="password">Senha</FieldLabel>
              <Input
                id="password"
                type="password"
                value={data.password}
                onChange={(e) => setData("password", e.target.value)}
                required
              />
              {errors.password && (
                <p className="text-sm text-destructive">{errors.password}</p>
              )}
            </Field>
            <Field>
              <FieldLabel htmlFor="password_confirmation">
                Confirmar Senha
              </FieldLabel>
              <Input
                id="password_confirmation"
                type="password"
                value={data.password_confirmation}
                onChange={(e) =>
                  setData("password_confirmation", e.target.value)
                }
                required
              />
              {errors.password_confirmation && (
                <p className="text-sm text-destructive">
                  {errors.password_confirmation}
                </p>
              )}
            </Field>
          </FieldGroup>
        </form>
      </CardContent>
      <CardFooter className="justify-center">
        <Button type="submit" form="register-form" disabled={processing}>
          Criar Conta e Aceitar
        </Button>
      </CardFooter>
    </Card>
  );
}

export default function Show({
  invitation,
  userExists,
  signedIn,
  emailMismatch,
  error,
}: Props) {
  const errorMessages: Record<string, { title: string; description: string }> = {
    not_found: {
      title: "Convite não encontrado",
      description: "Este link de convite é inválido.",
    },
    expired: {
      title: "Convite expirado",
      description: "Este convite expirou. Solicite um novo convite ao administrador.",
    },
    accepted: {
      title: "Convite já aceito",
      description: "Este convite já foi aceito anteriormente.",
    },
  };

  return (
    <main>
      <Head title="Aceitar Convite" />
      <div className="bg-muted flex min-h-svh flex-col items-center justify-center gap-6 p-6 md:p-10">
        <div className="flex w-full max-w-sm flex-col items-center gap-6">
          <a
            href="#"
            className="flex items-center gap-2 self-center font-medium"
          >
            <div className="bg-primary text-primary-foreground flex size-6 items-center justify-center rounded-md">
              <GalleryVerticalEnd className="size-4" />
            </div>
            {invitation?.organizationName ?? "Convite"}
          </a>

          {error && errorMessages[error] ? (
            <ErrorCard {...errorMessages[error]} />
          ) : emailMismatch && invitation ? (
            <ErrorCard
              title="E-mail diferente"
              description={`Este convite foi enviado para ${invitation.email}. Faça logout e entre com a conta correta.`}
            />
          ) : invitation && userExists && signedIn ? (
            <AcceptCard invitation={invitation} />
          ) : invitation && userExists && !signedIn ? (
            <SignInCard invitation={invitation} />
          ) : invitation ? (
            <RegisterCard invitation={invitation} />
          ) : (
            <ErrorCard
              title="Erro"
              description="Ocorreu um erro ao processar o convite."
            />
          )}
        </div>
      </div>
    </main>
  );
}
